"use client";

import React, { useEffect, useRef } from 'react';

// Particle data — bioluminescent specks in the water column
const PARTICLES = [
  { x: '12%', y: '30%', dur: '9s', delay: '0s', size: 1.5 },
  { x: '28%', y: '55%', dur: '12s', delay: '1.5s', size: 1 },
  { x: '45%', y: '20%', dur: '8s', delay: '3s', size: 2 },
  { x: '60%', y: '65%', dur: '11s', delay: '0.5s', size: 1 },
  { x: '75%', y: '40%', dur: '10s', delay: '2s', size: 1.5 },
  { x: '88%', y: '25%', dur: '7s', delay: '4s', size: 1 },
  { x: '20%', y: '78%', dur: '13s', delay: '1s', size: 2 },
  { x: '52%', y: '85%', dur: '9s', delay: '2.5s', size: 1 },
  { x: '83%', y: '72%', dur: '11s', delay: '0s', size: 1.5 },
  { x: '35%', y: '10%', dur: '8s', delay: '3.5s', size: 1 },
  { x: '68%', y: '88%', dur: '14s', delay: '1s', size: 2 },
  { x: '7%', y: '60%', dur: '10s', delay: '4.5s', size: 1 },
];

export default function HeroSection() {
  const heroRef = useRef<HTMLElement>(null);
  const headlineRef = useRef<HTMLDivElement>(null);

  // Subtle parallax on scroll
  useEffect(() => {
    const handleScroll = () => {
      if (!heroRef.current) return;
      const scrollY = window.scrollY;
      const glowEl = heroRef.current.querySelector('.hero-glow') as HTMLElement;
      if (glowEl) {
        glowEl.style.transform = `translateY(${scrollY * 0.15}px)`;
      }
    };
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // Entrance animation
  useEffect(() => {
    const el = headlineRef.current;
    if (!el) return;
    const spans = el.querySelectorAll<HTMLElement>('.hero-word');
    spans.forEach((span, i) => {
      span.style.opacity = '0';
      span.style.transform = 'translateY(40px)';
      span.style.transition = `opacity 1.2s cubic-bezier(0.16,1,0.3,1) ${i * 0.12}s, transform 1.2s cubic-bezier(0.16,1,0.3,1) ${i * 0.12}s`;
      // Small delay to allow paint
      requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          span.style.opacity = '1';
          span.style.transform = 'translateY(0)';
        });
      });
    });
  }, []);

  return (
    <section
      ref={heroRef}
      className="relative min-h-screen flex flex-col justify-center overflow-hidden ocean-bg"
      aria-label="Hero"
    >
      {/* ─── Deep-ocean ambient glows ─── */}
      <div className="hero-glow pointer-events-none absolute inset-0 overflow-hidden" aria-hidden="true">
        {/* Primary deep glow — bottom center */}
        <div
          className="glow-orb absolute bottom-0 left-1/2 -translate-x-1/2 w-[900px] h-[500px] rounded-full"
          style={{
            background: 'radial-gradient(ellipse, rgba(0,102,204,0.18) 0%, rgba(0,212,200,0.06) 40%, transparent 70%)',
            filter: 'blur(60px)',
          }}
        />
        {/* Secondary teal accent — left */}
        <div
          className="absolute top-1/3 -left-40 w-[500px] h-[400px] rounded-full"
          style={{
            background: 'radial-gradient(ellipse, rgba(0,212,200,0.07) 0%, transparent 65%)',
            filter: 'blur(80px)',
            animation: 'glow-breathe 8s ease-in-out infinite 2s',
          }}
        />
        {/* Right accent */}
        <div
          className="absolute top-1/4 -right-20 w-[400px] h-[350px] rounded-full"
          style={{
            background: 'radial-gradient(ellipse, rgba(0,40,100,0.25) 0%, transparent 65%)',
            filter: 'blur(100px)',
          }}
        />
      </div>

      {/* ─── Bioluminescent particles ─── */}
      <div className="pointer-events-none absolute inset-0 overflow-hidden" aria-hidden="true">
        {PARTICLES.map((p, i) => (
          <div
            key={i}
            className="particle"
            style={{
              left: p.x,
              top: p.y,
              width: `${p.size}px`,
              height: `${p.size}px`,
              '--duration': p.dur,
              '--delay': p.delay,
              opacity: 0.4,
              boxShadow: `0 0 ${p.size * 3}px rgba(0,212,200,0.8)`,
            } as React.CSSProperties}
          />
        ))}
      </div>

      {/* ─── Scan line decoration ─── */}
      <div
        className="pointer-events-none absolute inset-0 overflow-hidden"
        aria-hidden="true"
      >
        <div
          style={{
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            height: '1px',
            background: 'linear-gradient(90deg, transparent 0%, rgba(0,212,200,0.2) 50%, transparent 100%)',
            animation: 'scan-line 12s linear infinite 3s',
          }}
        />
      </div>

      {/* ─── Vertical accent line ─── */}
      <div
        className="pointer-events-none absolute left-[11%] top-0 bottom-0 w-px hidden lg:block"
        aria-hidden="true"
        style={{ background: 'linear-gradient(to bottom, transparent, rgba(0,212,200,0.08) 20%, rgba(0,212,200,0.08) 80%, transparent)' }}
      />

      {/* ─── Main content ─── */}
      <div className="relative z-10 max-w-7xl mx-auto px-5 lg:px-12 w-full pt-28 md:pt-32 pb-20 md:pb-24">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-16 items-end">

          {/* Left — Headline */}
          <div className="lg:col-span-8">
            {/* Eyebrow */}
            <div
              className="section-label mb-7 md:mb-10 flex items-center gap-3 hero-word"
              style={{ opacity: 1 }}
            >
              <div style={{ width: '24px', height: '1px', background: 'var(--teal-glow)', opacity: 0.6 }} />
              Autonomous Survey Platform
            </div>

            {/* Headline */}
            <div ref={headlineRef} className="mb-8 md:mb-12">
              <h1 className="hero-display text-seafoam" style={{ display: 'block' }}>
                <span className="hero-word block">Autonomous systems</span>
                <span className="hero-word block">for offshore and</span>
                <span className="hero-word block">coastal <span className="teal-glow-text hero-word-large">environments</span></span>
              </h1>
            </div>

            {/* Sub-text */}
            <p
              className="hero-word max-w-lg text-base leading-relaxed"
              style={{ color: 'rgba(232, 237, 244, 0.85)', fontWeight: 300, lineHeight: 1.75 }}
            >
              Corridor coverage efficiency and repeatable data acquisition — from planning through to deliverables.
            </p>

            {/* Mobile CTA — shown only on small screens */}
            <a
              href="#contact"
              className="mt-8 flex md:hidden items-center justify-center gap-3 py-4 border border-[rgba(0,212,200,0.3)]"
              style={{
                background: 'rgba(0,212,200,0.04)',
                fontSize: '0.65rem',
                letterSpacing: '0.22em',
                fontWeight: 600,
                textTransform: 'uppercase',
                color: 'var(--teal-glow)',
                fontFamily: 'DM Mono, monospace',
              }}
            >
              Enquire Now
              <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                <path d="M5 12h14M12 5l7 7-7 7" />
              </svg>
            </a>
          </div>

          {/* Right — Metadata panel */}
          <div className="lg:col-span-4 hidden lg:flex flex-col gap-6">
            {/* Coordinate-style readout */}
            <div
              className="glass-ocean rounded-sm p-5"
              style={{ fontFamily: 'DM Mono, monospace' }}
            >
              <div className="section-label mb-4" style={{ fontSize: '0.55rem' }}>System Status</div>
              <div className="space-y-2.5">
                {[
                  { label: 'Mode', value: 'In Development' },
                  { label: 'Tasking', value: 'Survey Type' },
                  { label: 'Acquisition', value: 'Repeatable' },
                  { label: 'Ops Mode', value: 'Turnkey / Day-Rate' },
                ].map(({ label, value }) => (
                  <div key={label} className="flex items-center justify-between">
                    <span style={{ fontSize: '0.6rem', color: 'rgba(138,151,168,0.85)', letterSpacing: '0.1em' }}>
                      {label}
                    </span>
                    <span style={{ fontSize: '0.65rem', color: 'var(--teal-glow)', letterSpacing: '0.08em' }}>
                      {value}
                    </span>
                  </div>
                ))}
              </div>
              {/* Pulsing indicator */}
              <div className="mt-4 pt-3 border-t border-[rgba(0,212,200,0.1)] flex items-center gap-2">
                <div className="relative flex">
                  <span className="animate-ping absolute inline-flex h-2 w-2 rounded-full bg-teal-glow opacity-50" style={{ background: 'var(--teal-glow)' }} />
                  <span className="relative inline-flex rounded-full h-2 w-2" style={{ background: 'var(--teal-glow)' }} />
                </div>
                <span style={{ fontSize: '0.55rem', color: 'rgba(0,212,200,0.75)', letterSpacing: '0.15em', fontFamily: 'DM Mono, monospace' }}>
                  ADVANCING
                </span>
              </div>
            </div>

            {/* CTA */}
            <a
              href="#contact"
              className="flex items-center justify-center gap-3 py-4 border border-[rgba(0,212,200,0.3)] transition-all duration-400 group"
              style={{
                background: 'rgba(0,212,200,0.04)',
                fontSize: '0.65rem',
                letterSpacing: '0.22em',
                fontWeight: 600,
                textTransform: 'uppercase',
                color: 'var(--teal-glow)',
                fontFamily: 'DM Mono, monospace',
              }}
              onMouseEnter={e => {
                (e.currentTarget as HTMLElement).style.background = 'rgba(0,212,200,0.09)';
                (e.currentTarget as HTMLElement).style.borderColor = 'rgba(0,212,200,0.55)';
              }}
              onMouseLeave={e => {
                (e.currentTarget as HTMLElement).style.background = 'rgba(0,212,200,0.04)';
                (e.currentTarget as HTMLElement).style.borderColor = 'rgba(0,212,200,0.3)';
              }}
            >
              Enquire Now
              <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                <path d="M5 12h14M12 5l7 7-7 7" />
              </svg>
            </a>
          </div>
        </div>

        {/* ─── Bottom scroll indicator ─── */}
        <div className="absolute bottom-10 left-1/2 -translate-x-1/2 flex flex-col items-center gap-2" aria-hidden="true">
          <div style={{ width: '1px', height: '40px', background: 'linear-gradient(to bottom, rgba(0,212,200,0.4), transparent)' }} />
          <span style={{ fontFamily: 'DM Mono, monospace', fontSize: '0.5rem', letterSpacing: '0.25em', color: 'rgba(107,122,141,0.5)', textTransform: 'uppercase' }}>
            Scroll
          </span>
        </div>
      </div>

      {/* ─── Bottom gradient fade ─── */}
      <div
        className="pointer-events-none absolute bottom-0 left-0 right-0 h-40"
        aria-hidden="true"
        style={{ background: 'linear-gradient(to bottom, transparent, var(--abyss))' }}
      />
    </section>
  );
}